import type { ToolDefinitionType } from "@openai/realtime-api-beta/dist/lib/client";
import { z } from "zod";

export const InsertScriptItemSchema = z.object({
  scriptItem: z.string(),
  reason: z.string(),
  confidence: z.number(),
  context: z.string(),
});

export const InsertScriptItemMessageSchema = z.object({
  type: z.literal("insert_script_item"),
  data: InsertScriptItemSchema,
});

export const SkipToScriptItemSchema = z.object({
  scriptItem: z.string(),
  reason: z.string(),
});

export const SkipToScriptItemMessageSchema = z.object({
  type: z.literal("skip_to_script_item"),
  data: SkipToScriptItemSchema,
});

export const WarmUpNextScriptItemSchema = z.object({
  scriptItem: z.string(),
});

export const WarmUpNextScriptItemMessageSchema = z.object({
  type: z.literal("warm_up_next_script_item"),
  data: WarmUpNextScriptItemSchema,
});

export const CompleteScriptItemSchema = z.object({
  success: z.boolean(),
  reason: z.string(),
});

export const CompleteScriptItemMessageSchema = z.object({
  type: z.literal("complete_script_item"),
  data: CompleteScriptItemSchema,
});

export const CallRecapSchema = z.object({
  recap: z.array(
    z.object({
      title: z.string(),
      description: z.string(),
    }),
  ),
});

export const CallRecapMessageSchema = z.object({
  type: z.literal("call_recap"),
  data: CallRecapSchema,
});

export const CallCompleteSchema = z.object({
  outcome: z.enum(["successful", "unsuccessful", "follow_up_needed"]),
  reason: z.string(),
});

export const CallCompleteMessageSchema = z.object({
  type: z.literal("call_complete"),
  data: CallCompleteSchema,
});

export type InsertScriptItem = z.infer<typeof InsertScriptItemSchema>;
export type SkipToScriptItem = z.infer<typeof SkipToScriptItemSchema>;
export type WarmUpNextScriptItem = z.infer<typeof WarmUpNextScriptItemSchema>;
export type CompleteScriptItem = z.infer<typeof CompleteScriptItemSchema>;
export type CallRecap = z.infer<typeof CallRecapSchema>;
export type CallComplete = z.infer<typeof CallCompleteSchema>;

export const sdrInstructions = `You are an AI assistant that helps a Sales Development Representative to have a video call with a potential customer.
The SDR is using an application which has an interactive user interface which presents the SDR with a script to follow.
During the call you are listening in to what the SDR and the customer are saying, by receiving real-time transcript events and any interactions that the SDR makes in the application.
      
At the start of the call, you will be provided with:
* A script outline which describes what the SDR should say when on a video call with the potential customer
* Some background information about the customer, including their name, company, and role
* Some additional information about the SDR, including their name, company, and role
* Some information about the product or service being sold

During the call you will receive:
* Real-time transcript events from the call, including both the SDR and the customer's speech. There may be more than one customer on the call.
* Real-time events from the SDR, including any actions they take in the application they are using.

During the call you will have several tasks to complete:

## Task 1: Provide script items to the SDR
The SDR will follow the script that you provide.
To start the call, provide the first script item and the continue providing new script items as appropriate during the call.
You can suggest script items to the SDR by using the 'insert_script_item' tool.
Initially, the script will be empty, so you will need to provide the first script item.
The SDR will read out the suggested script item to the customer.
You will then provide the next script item to the SDR, and so on.

## Task 2: "Warm up" the next script item that the SDR will likely need to say next
You can use the 'warm_up_next_script_item' tool to suggest the next script item that the SDR will likely need to say next.
This will help the SDR to transition smoothly to the next part of the script without waiting for you to generate the next item.
If you respond with a new script item it will be replace the item you previously provided as the warm up item.

## Task 3: Detect when script items are complete
Based on the transcript, if you detect that the SDR has completed a script item, you can use the 'complete_script_item' tool to notify the system that the SDR has finished reading out the script item.
This task might need to happen simultaneously with the 'warm_up_next_script_item' task, so that if the customer responds very quickly, the SDR can read out the next script item without waiting for your recommendation.
In some cases, the script item might be completed in the sense that he SDR has read it out, but the actual purpose of the item has not been achieved.
For instance, a question asking about the customer's budget might be completed if the SDR asks the question and the customer responds, even if the customer does not provide a specific budget figure.
In this case, you should use the 'complete_script_item' tool to notify the system that the SDR has finished reading out the script item, even though the SDR has not yet provided the information you need.
You should then use the 'insert_script_item' tool to suggest a new script item to the SDR to ask for the missing information.

## Task 4: Help the sales person adapt to customer interactions
The script outline you will be provided with is a guide for the SDR to follow, but the conversation may not always go as planned.
Follow the instructions in the script so that the SDR can adapt to the customer's responses and ask follow-up questions as needed.

## Task 5: Allow the sales person to skip to another part of the script
The salesperson might decide to jump to another part of the script based on the customer's responses.
You can use the 'skip_to_script_item' tool to suggest that the SDR skip to another part of the script.

## Task 6: Detect when the sales person has skipped to another part of the script
The user might go off script and say something that is not part of the script.
If this happens, listen to the conversation and work out where they have jumped to.
Pick up from there with the next script item you suggest.

## Task 7: Help the SDR to summarise the call for the customer
At the end of the call, the SDR should summarise the call for the customer.
This involves the SDR reading out the answers to the questions in the script and double checking them with the customer.
You can use the 'call_recap' tool to provide this recap so the SDR can read it out.

## Task 8: Detect when the call is complete
The call is complete when the SDR has reached the end of the script and the customer has said goodbye.
You can use the 'call_complete' tool to notify the system that the call is complete.

## Rules
* You should only respond with the tools you are instructed to use.
* You should not respond with any other text.
* You should not respond with any other tools.
* You should not respond with any other instructions.
* Use professional language and tone, but be friendly and engaging. Adapt to the conversational tone of the SDR.
* The SDR cannot interact with you directly, so do not respond with any questions for the SDR.

## Aims
The overall aims of the call are:
* To establish if the customer is interested in the product or service being sold
* To qualify that the customer is the right person to talk to, and that they have the authority to make a buying decision
* To qualify that the customer has the budget to buy the product or service
* To qualify that the customer's company is the right fit for the product or service
* If these are true, then to arrange the next steps in the sales process

The call is successful if the SDR has established all of these points. An unsuccessful call is one where the SDR has not established all of these points.

Do your best to help the SDR to have a successful call with the customer.`;

export const insertScriptItemTool: ToolDefinitionType = {
  name: "insert_script_item",
  description:
    "Notify the system that the user should adjust the script in response to the current conversation by inserting a new script item",
  parameters: {
    type: "object",
    properties: {
      scriptItem: {
        type: "string",
        description: "The new script item to be inserted",
      },
    },
    required: ["scriptItem", "reason", "confidence", "context"],
  },
};

export const suggestSkipToScriptItemTool: ToolDefinitionType = {
  name: "suggest_skip_to_script_item",
  description:
    "Notify the user that they could potentially skip to another part of the script",
  parameters: {
    type: "object",
    properties: {
      scriptItem: {
        type: "string",
        description: "The text of the new script item",
      },
      reason: {
        type: "string",
        description: "The reason why the user should skip to this script item",
      },
    },
    required: ["scriptItem", "reason"],
  },
};

export const warmUpNextScriptItemTool: ToolDefinitionType = {
  name: "warm_up_next_script_item",
  description:
    "Notify the system that if the current item is completed quickly, the SDR could immediately move on to this as the next item",
  parameters: {
    type: "object",
    properties: {
      scriptItem: {
        type: "string",
        description:
          "The new item that will be displayed to the SDR as the potential next script item",
      },
    },
    required: ["scriptItem"],
  },
};

export const completeScriptItemTool: ToolDefinitionType = {
  name: "complete_script_item",
  description:
    "Notify the system that the SDR has finished reading out the current script item",
  parameters: {
    type: "object",
    properties: {
      success: {
        type: "boolean",
        description:
          "Whether the objective of the script item was achieved, not just whether it was read out",
      },
      reason: {
        type: "string",
        description: "Why this script item is considered complete",
      },
    },
    required: ["success", "reason"],
  },
};

export const callRecapTool: ToolDefinitionType = {
  name: "call_recap",
  description:
    "Provide a recap of the key points from the call for the SDR to read back to the customer",
  parameters: {
    type: "object",
    properties: {
      recap: {
        type: "array",
        items: {
          type: "object",
          properties: {
            title: {
              type: "string",
              description:
                "A brief title for the key point. For instance 'Budget' or 'Timing'",
            },
            description: {
              type: "string",
              description:
                "A description of the key point. For instance '£100,000' or 'Some time in Q1'. If an item is not yet confirmed, you can use '?' to indicate that",
            },
            status: {
              type: "string",
              enum: ["confirmed", "unconfirmed", "not_discussed"],
              description:
                "The status of this item during the call: 'confirmed' if sufficiently answered, 'unconfirmed' if discussed but not fully resolved, or 'not_discussed' if not addressed",
            },
          },
          required: ["title", "description"],
        },
        description:
          "A recap the key points discussed during the call that should have been discussed, based on the script outline",
      },
    },
    required: ["recap"],
  },
};

export const callCompleteTool: ToolDefinitionType = {
  name: "call_complete",
  description:
    "Notify the system that the call has reached its natural conclusion",
  parameters: {
    type: "object",
    properties: {
      outcome: {
        type: "string",
        enum: ["successful", "unsuccessful", "follow_up_needed"],
        description: "The final outcome of the call",
      },
      reason: {
        type: "string",
        description:
          "Why the call is considered complete and the outcome determination",
      },
    },
    required: ["outcome", "reason"],
  },
};

export const tools = [
  insertScriptItemTool,
  suggestSkipToScriptItemTool,
  warmUpNextScriptItemTool,
  completeScriptItemTool,
  callRecapTool,
  callCompleteTool,
];

export const sdrScript = `This playbook is based around the BANT methodology,
Budget, Authority, Need and Timing.

Summary
The primary role of an SDR in a prospective sales call is to qualify BANT with the prospect. The results of this call should determine if the prospect is moved into an SQL (Sales Qualified Lead) and passed to the AE/meeting booked for the AE or determined not to be a fit and nurtured by marketing.

Essentially the SDR needs to know if the prospect has authority to purchase, if they are the only person needed to make a purchasing decision, what the pain is they want to solve and the consequences of leaving it unsolved, ‘why now’ and if they are ready to purchase and confirming the prospect has or has access to a budget.

If any of the above is uncovered or doesn’t return the requisite response, this is not an SQL and should not be progressed into a meeting or SQL.

For the benefit of running a conversational call rather than a scripted set of questions, BANT is reordered on the call.

Inbound SDR Call Script & Decision Tree
Goal: Qualify using BANT. The prospect has already requested a call. Confirm BANT and either schedule an AE call or send info if not ready.
Call Opening
SDR: “Hi [Name], thanks for reaching out about [Product/Service]. Could you tell me more about what led you to book this call?”

Establish Need
SDR: “What’s the main problem you’re looking to solve?”
If critical/urgent problem:
SDR: “Understood. Why is solving this a priority now?”
If minor or not urgent:
SDR: “Got it. Are you just exploring options for now, or is there a pressing issue?”
If no urgency:
SDR: “Understood. I can send over info and we can reconnect when it’s more urgent. Does that make sense?”
[If yes, end call. If no, confirm if a later follow-up is useful.]

Timing
SDR: “If we were to help solve [problem], by what exact date would you want to start implementing a solution?”
If Prospect provides a specific date: Move to Authority
If Prospect is vague (e.g., ‘in a few months’):
SDR: “To plan effectively, we need a target start date. Could you share a specific month and day you’d like to begin?”
If still no exact date:
SDR: “I understand it’s hard to pinpoint now. Should we set a reminder to reconnect when you have a firm date?”
[If yes, schedule follow-up. If no, end politely.]

Authority
SDR: “Are you the main decision-maker for this purchase?”
If Yes: Move to Budget
If No:
SDR: “Understood. Who is the exact decision-maker by name?”
If they provide a name (e.g. “It’s Sarah Jones, our Head of Sales”):
SDR: “Great. Can Sarah join our next call?”
If yes: Move to Budget
If no:
SDR: “Having Sarah’s input is important. Could I at least share info with her directly? What’s her email?”
[If email provided, proceed to Budget. If not provided, note obstacle and consider follow-up later.]
If Prospect is vague (e.g. “My manager”):
SDR: “I’ll need their exact name to ensure we involve the right person. Could you confirm their name and role?”
If still no name given:
SDR: “Without knowing who the decision-maker is, it’s tough to move forward. Let’s reconnect when you can confirm who will make the call.”
[End or schedule follow-up when they know the decision-maker.]

Budget
SDR: “Is there a dedicated budget allocated for this project?”
If Yes:
SDR: “Great. Can you share the approximate amount allocated?”
If a figure is given: Move to Recap
If they won’t share a figure:
SDR: “Even a rough range helps us tailor our solution. Without a figure, are you flexible on budget?”
[If still no info, note uncertainty and move to Recap anyway.]
If No:
SDR: “Understood. How do you plan to fund this solution if no budget is currently allocated?”
If they have a plan to secure funding soon: Move to Recap
If no plan/funding unclear:
SDR: “I see. Let’s reconnect when you have more clarity on funding.”
[Schedule follow-up if appropriate, otherwise end call.]

Recap
SDR: “Let me recap what we’ve discussed:
Problem: [X]
Priority: [Y - Why now?]
Start date: [Z - Exact date or timeframe agreed]
Decision-maker: [Name(s)]
Budget: [Figure or plan]
Do I have all of that right? Anything I’ve missed or misunderstood?”
If Prospect confirms: Move to Next Steps
If Prospect clarifies: Adjust details and confirm again.

Next Steps
SDR: “Perfect. Based on this, I’d like to schedule a deeper conversation with one of our AEs to see how we can help you achieve [desired outcome]. Does [Date/Time] work?”
If Yes: Book meeting, confirm attendees and emails.
If No: Suggest alternative times or a follow-up date once conditions are met.

End of Inbound Script`;
